/*
 * Peter Beales Roses - approximate-location Garden Centre locator visibility
 * ---------------------------------------------------------------------------
 * The header's "My store" / Garden Centre locator trigger is most useful to
 * shoppers actually near the Norfolk garden centre. This hides that trigger
 * for visitors who don't look Norfolk-based, while leaving the underlying
 * store-selector section/modal (sections/store-selector.liquid,
 * component-store-selector.js) completely untouched - only the header
 * trigger's visibility changes.
 *
 * Method: a single, permission-free lookup against BigDataCloud's free
 * client-side reverse-geocode API, called WITHOUT coordinates so it never
 * triggers the browser's location-permission prompt - it silently resolves
 * an approximate location from the visitor's IP instead. Free, no API key,
 * no published rate limit. Cached in localStorage for ~24h so it's one
 * lookup per visitor per day, not per page view.
 *
 * Honest limitation: IP geolocation cannot reliably resolve county-level
 * location. Accuracy degrades near the Norfolk border, for VPN users, and
 * on mobile networks that route through distant NAT gateways. On any
 * failure, timeout, or ambiguous result this fails OPEN (shows the
 * trigger) rather than hiding it from a real Norfolk customer.
 *
 * Debug override (dev/testing only - not for production use):
 *   ?debug_geo=norfolk   forces the "show" branch
 *   ?debug_geo=elsewhere forces the "hide" branch
 *
 * Public API for other scripts that want the same signal (e.g. the
 * click-and-collect availability badge in templates/product.json,
 * "custom_liquid_8g9rWy" block, which only claims in-store collection is
 * ready for visitors who look Norfolk-based):
 *   window.pbGeo.isNearGardenCentre()  -> true | false | null (not yet known)
 *   document 'pb:geo-resolved' event, detail: { near: true|false }, fired
 *   every time a value is determined (cache hit, fresh lookup, debug
 *   override, or fail-open default) - listen for this to repaint once the
 *   value is known, since it may resolve after your own first render.
 *   "true" covers both a genuine Norfolk match AND the fail-open default -
 *   callers that want to distinguish "confirmed not near" from "unknown"
 *   should treat anything except a strict `=== false` as "assume yes".
 *
 * Styling: assets/pb-enhancements.css (.pb-geo-hidden)
 */
(function () {
  'use strict';

  var CACHE_KEY = 'pb-geo-norfolk';
  var CACHE_MS = 24 * 60 * 60 * 1000;
  var ENDPOINT = 'https://api.bigdatacloud.net/data/reverse-geocode-client';
  var TARGET_SELECTOR = '[data-type="store-selector"]';
  var HIDE_CLASS = 'pb-geo-hidden';

  // NR = Norwich/Norfolk in full. PE is NOT used whole - it also covers
  // Peterborough/Lincolnshire (e.g. PE1-29, including Boston PE21-22, which
  // are not Norfolk) - only PE30-38 (King's Lynn & West Norfolk) count.
  var NORFOLK_PLACE_SIGNALS = ['norfolk', 'norwich', 'attleborough', 'thetford', 'king\'s lynn', 'kings lynn', 'great yarmouth', 'dereham'];

  function postcodeIsNorfolk(postcode) {
    var m = /^([A-Z]{1,2})(\d{1,2})/.exec(postcode);
    if (!m) return false;
    var area = m[1];
    var district = parseInt(m[2], 10);
    if (area === 'NR') return true;
    if (area === 'PE' && district >= 30 && district <= 38) return true;
    return false;
  }

  var lastKnown = null;

  function applyVisibility(isNorfolk) {
    var els = document.querySelectorAll(TARGET_SELECTOR);
    for (var i = 0; i < els.length; i++) {
      if (isNorfolk) {
        els[i].classList.remove(HIDE_CLASS);
      } else {
        els[i].classList.add(HIDE_CLASS);
      }
    }
    lastKnown = isNorfolk;
    try {
      document.dispatchEvent(new CustomEvent('pb:geo-resolved', { detail: { near: isNorfolk } }));
    } catch (e) {}
  }

  window.pbGeo = window.pbGeo || {};
  window.pbGeo.isNearGardenCentre = function () {
    return lastKnown;
  };

  function looksNorfolk(data) {
    if (!data) return null;
    try {
      var postcode = (data.postcode || '').toUpperCase().replace(/\s+/g, '');
      if (postcodeIsNorfolk(postcode)) return true;
      var haystack = [data.principalSubdivision, data.city, data.locality, data.localityInfo && data.localityInfo.administrative && data.localityInfo.administrative[0] && data.localityInfo.administrative[0].name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      for (var s = 0; s < NORFOLK_PLACE_SIGNALS.length; s++) {
        if (haystack.indexOf(NORFOLK_PLACE_SIGNALS[s]) !== -1) return true;
      }
      return false;
    } catch (e) {
      return null;
    }
  }

  function readCache() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.value !== 'boolean' || typeof parsed.at !== 'number') return null;
      if (Date.now() - parsed.at > CACHE_MS) return null;
      return parsed.value;
    } catch (e) {
      return null;
    }
  }

  function writeCache(value) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ value: value, at: Date.now() }));
    } catch (e) {}
  }

  function debugOverride() {
    try {
      var params = new URLSearchParams(window.location.search);
      var v = params.get('debug_geo');
      if (v === 'norfolk') return true;
      if (v === 'elsewhere') return false;
    } catch (e) {}
    return null;
  }

  function init() {
    var override = debugOverride();
    if (override !== null) {
      applyVisibility(override);
      return;
    }

    var cached = readCache();
    if (cached !== null) {
      applyVisibility(cached);
      return;
    }

    // Fail open by default while the lookup is in flight / if it never resolves.
    var settled = false;
    var failOpenTimer = setTimeout(function () {
      if (!settled) {
        settled = true;
        applyVisibility(true);
      }
    }, 2500);

    fetch(ENDPOINT, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (settled) return; // fail-open timer already fired
        clearTimeout(failOpenTimer);
        settled = true;
        var result = looksNorfolk(data);
        if (result === null) {
          applyVisibility(true); // ambiguous/failed - fail open
          return;
        }
        writeCache(result);
        applyVisibility(result);
      })
      .catch(function () {
        if (settled) return;
        clearTimeout(failOpenTimer);
        settled = true;
        applyVisibility(true); // fail open
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
